import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Flex, HStack, Image, Text, VStack } from "@chakra-ui/react";
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  Coins,
  Landmark,
  LayoutDashboard,
  LineChart,
  ListChecks,
  LogOut,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Users,
  UsersRound,
} from "lucide-react";
import SideMenuArray from "../../Utils/SideMenuArray";
import { getLocalStorageItem, getSideMenuIcon } from "../../Utils/Helper";
import { logout } from "../../Redux/Action/auth/logoutAction";
import { Tooltip } from "../ui/tooltip";
import brandMark from "../../Images/globalapex-mark.png";
import { getDomainConfig } from "../../Utils/dynamicTitleManager";

// Section order in the sidebar; items without a section go last
const SECTION_ORDER = ["Overview", "Users", "White Labels", "Finance", "Trading"];

const ICONS = {
  "/": LayoutDashboard,
  "/add-user": UserPlus,
  "/users-list": Users,
  "/manage-kyc": ShieldCheck,
  "/deletedusersList": UserMinus,
  "/corporate-kyc": Building2,
  "/corporate-users": UsersRound,
  "/add-white-labels": UserPlus,
  "/get-white-labels": ListChecks,
  "/deposits": ArrowDownToLine,
  "/withdrawals": ArrowUpFromLine,
  "/all-transactions": ArrowLeftRight,
  "/banking": Landmark,
  "/all-trades": LineChart,
  "/currency-pairs": Coins,
};

const ROLE_LABEL = { 1: "Super Admin", 2: "White Label Admin" };

const getInitials = (text = "") =>
  String(text)
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "A";

/** Filtered menu items for the logged-in role / white label (shared with the top bar). */
export const useMenuItems = () => {
  const userData = useSelector((state) => state?.Login?.userData);

  return useMemo(() => {
    // Get whitelabel data directly from localStorage to avoid Redux state timing issues
    const whitelabelData = localStorage.getItem("whitelabelData");
    let parsedWhitelabelData = null;

    try {
      parsedWhitelabelData = whitelabelData ? JSON.parse(whitelabelData) : null;
    } catch (error) {
      console.error("Error parsing whitelabelData from localStorage:", error);
    }

    return SideMenuArray?.filter((item) => {
      const { accessRoles, groupName } = item || {};

      // Check if user has access role
      const hasAccessRole =
        (Array.isArray(accessRoles) && accessRoles.length === 1 && accessRoles[0] === 0) ||
        (Array.isArray(accessRoles) && accessRoles.includes(userData?.role));

      // If item has groupName restriction, check if it matches the whitelabel groupName
      if (groupName) {
        return hasAccessRole && parsedWhitelabelData?.groupName === groupName;
      }

      return hasAccessRole;
    });
  }, [userData?.role]);
};

export const SideMenu = ({ isCollapsed, setCollapsed, isMobileOpen, setMobileOpen }) => {
  const userData = useSelector((state) => state?.Login?.userData);
  const screenWidth = useSelector((state) => state?.CurrentScreenWidth?.width);
  const loading = useSelector((state) => state?.GetWhiteLabel?.loading);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const menuItems = useMenuItems();
  const [logoSrc, setLogoSrc] = useState(null);

  const isMobile = screenWidth <= 768;
  const baseRoute = "/" + (location.pathname?.split("/")[1] || "");
  const whitelabel = getLocalStorageItem("whitelabelData");

  useEffect(() => {
    // The brand slot is ~56px wide next to the group name: a square mark reads
    // there, a wide wordmark does not.
    if (!loading) setLogoSrc(getDomainConfig().mark || whitelabel?.logo_light || brandMark);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = useMemo(() => {
    const grouped = {};
    (menuItems || []).forEach((item) => {
      const key = item.section || "More";
      (grouped[key] = grouped[key] || []).push(item);
    });
    return Object.keys(grouped)
      .sort((a, b) => {
        const ia = SECTION_ORDER.indexOf(a);
        const ib = SECTION_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map((title) => ({ title, items: grouped[title] }));
  }, [menuItems]);

  const handleNavigate = (url) => {
    navigate(url);
    if (isMobile) setMobileOpen(false);
  };

  const renderIcon = (item) => {
    const Icon = ICONS[item.url];
    return Icon ? <Icon size={18} strokeWidth={1.8} /> : getSideMenuIcon(item.icon);
  };

  const displayName = userData?.fname
    ? `${userData.fname} ${userData.lname || ""}`.trim()
    : userData?.user_name || userData?.email || "Admin";

  return (
    <Flex
      direction="column"
      height="100%"
      bg="bg.surface"
      borderRight="1px solid"
      borderColor="border.subtle"
      overflow="hidden"
    >
      {/* Brand */}
      <HStack
        h="64px"
        flexShrink={0}
        px={isCollapsed ? 0 : 5}
        gap={3}
        justifyContent={isCollapsed ? "center" : "flex-start"}
        borderBottom="1px solid"
        borderColor="border.subtle"
      >
        {logoSrc && (
          <Image
            src={logoSrc}
            alt="Logo"
            h={isCollapsed ? "28px" : "32px"}
            maxW={isCollapsed ? "40px" : "56px"}
            objectFit="contain"
          />
        )}
        {!isCollapsed && (
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="700" color="fg.default" lineClamp={1} letterSpacing="-0.01em">
              {whitelabel?.groupName || "Admin"}
            </Text>
            <Text fontSize="xs" color="fg.subtle" lineClamp={1}>
              Admin Console
            </Text>
          </Box>
        )}
      </HStack>

      {/* Navigation */}
      <VStack as="nav" align="stretch" gap={5} px={3} py={4} overflowY="auto" flex="1">
        {sections.map((section) => (
          <Box key={section.title}>
            {isCollapsed ? (
              <Box h="1px" bg="border.subtle" mx={2} mb={2} />
            ) : (
              <Text
                px={3}
                mb={1.5}
                fontSize="11px"
                fontWeight="600"
                letterSpacing="0.06em"
                textTransform="uppercase"
                color="fg.subtle"
              >
                {section.title}
              </Text>
            )}

            <VStack align="stretch" gap={0.5}>
              {section.items.map((item) => {
                const active = baseRoute === item.url;
                const row = (
                  <HStack
                    key={item.url}
                    as="button"
                    type="button"
                    onClick={() => handleNavigate(item.url)}
                    aria-current={active ? "page" : undefined}
                    position="relative"
                    w="100%"
                    h="38px"
                    px={isCollapsed ? 0 : 3}
                    gap={3}
                    justifyContent={isCollapsed ? "center" : "flex-start"}
                    borderRadius="lg"
                    cursor="pointer"
                    bg={active ? "brand.50" : "transparent"}
                    color={active ? "brand.700" : "fg.muted"}
                    fontWeight={active ? "600" : "500"}
                    _hover={{ bg: active ? "brand.50" : "gray.100", color: active ? "brand.700" : "fg.default" }}
                    _focusVisible={{ outline: "2px solid", outlineColor: "brand.500", outlineOffset: "1px" }}
                    transition="background 140ms ease, color 140ms ease"
                    _before={
                      active
                        ? {
                            content: '""',
                            position: "absolute",
                            left: isCollapsed ? "-12px" : "-12px",
                            top: "8px",
                            bottom: "8px",
                            w: "3px",
                            borderRightRadius: "full",
                            bg: "brand.600",
                          }
                        : undefined
                    }
                  >
                    <Box display="flex" flexShrink={0} fontSize="18px">
                      {renderIcon(item)}
                    </Box>
                    {!isCollapsed && (
                      <Text fontSize="sm" lineClamp={1} textAlign="left">
                        {item.title}
                      </Text>
                    )}
                  </HStack>
                );

                return isCollapsed ? (
                  <Tooltip key={item.url} content={item.title} positioning={{ placement: "right" }} showArrow>
                    {row}
                  </Tooltip>
                ) : (
                  row
                );
              })}
            </VStack>
          </Box>
        ))}
      </VStack>

      {/* Footer: account + collapse */}
      <Box borderTop="1px solid" borderColor="border.subtle" p={3} flexShrink={0}>
        <HStack gap={3} justifyContent={isCollapsed ? "center" : "flex-start"}>
          <Flex
            flexShrink={0}
            w="34px"
            h="34px"
            borderRadius="full"
            bg="brand.100"
            color="brand.700"
            fontSize="xs"
            fontWeight="700"
            align="center"
            justify="center"
          >
            {getInitials(displayName)}
          </Flex>
          {!isCollapsed && (
            <>
              <Box minW={0} flex="1">
                <Text fontSize="sm" fontWeight="600" color="fg.default" lineClamp={1}>
                  {displayName}
                </Text>
                <Text fontSize="xs" color="fg.subtle" lineClamp={1}>
                  {ROLE_LABEL[userData?.role] || "Admin"}
                </Text>
              </Box>
              <Tooltip content="Log out" showArrow>
                <Box
                  as="button"
                  type="button"
                  aria-label="Log out"
                  onClick={() => dispatch(logout())}
                  p={2}
                  borderRadius="md"
                  color="fg.subtle"
                  cursor="pointer"
                  _hover={{ bg: "red.50", color: "red.600" }}
                >
                  <LogOut size={16} />
                </Box>
              </Tooltip>
            </>
          )}
        </HStack>

        {!isMobile && (
          <HStack
            as="button"
            type="button"
            onClick={() => setCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            mt={3}
            w="100%"
            h="32px"
            gap={2}
            justifyContent="center"
            borderRadius="md"
            color="fg.subtle"
            fontSize="xs"
            fontWeight="500"
            cursor="pointer"
            _hover={{ bg: "gray.100", color: "fg.muted" }}
          >
            {isCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            {!isCollapsed && <Text>Collapse</Text>}
          </HStack>
        )}
      </Box>
    </Flex>
  );
};
